import { PageAccessEntryRecord } from "models/view-models/page-access-entry-record";
import { useManageIndividualsWithPageAccess } from "organisms/modals/page-access-modal/hooks/use-manage-individuals-with-page-access";
import { useManagePageAccessDetails } from "organisms/modals/page-access-modal/hooks/use-manage-page-access-details";
import { useCallback, useMemo } from "react";

interface UseManagePageAccess {
    accessRecipients: PageAccessEntryRecord[];
    publicPageLink: string;
    clearErrors: () => void;
    errors: string[];
    isPubliclyAccessible: boolean;
    isCloningPermitted: boolean;
    loading: boolean;
    saveChanges: (
        isPubliclyAccessible: boolean,
        isCloningPermitted: boolean
    ) => Promise<boolean>;
    stageNewRecipients: (newRecipientEmails: string[]) => void;
    removeRecipient: (recipient: PageAccessEntryRecord) => void;
    refreshPageAccess: () => Promise<void>;
    clearUnpersistedChanges: () => void;
}

export const useManagePageAccess = (
    pageId: number | null
): UseManagePageAccess => {
    const {
        clearErrors: clearPageAccessDetailsErrors,
        errors: pageAccessDetailsErrors,
        isPubliclyAccessible,
        isCloningPermitted,
        loading: pageAccessDetailsLoading,
        updatePageAccessDetails,
        publicPageLink,
        refreshPage,
    } = useManagePageAccessDetails(pageId);
    const {
        accessRecipients,
        clearErrors: clearIndividualsWithAccessErrors,
        errors: individualsWithAccessErrors,
        loading: individualsWithAccessLoading,
        saveChanges: saveChangesToIndividualsWithAccess,
        stageNewRecipients,
        removeRecipient,
        refreshAccessRecipients,
        clearUnpersistedChanges,
    } = useManageIndividualsWithPageAccess(pageId);

    const clearErrors = () => {
        clearPageAccessDetailsErrors();
        clearIndividualsWithAccessErrors();
    };

    const errors = useMemo(
        () => [...pageAccessDetailsErrors, ...individualsWithAccessErrors],
        [individualsWithAccessErrors, pageAccessDetailsErrors]
    );

    const loading = useMemo(
        () => pageAccessDetailsLoading || individualsWithAccessLoading,
        [individualsWithAccessLoading, pageAccessDetailsLoading]
    );

    const saveChanges = useCallback(
        async (
            isPubliclyAccessible: boolean,
            isCloningPermitted: boolean
        ): Promise<boolean> => {
            const updateActions = [
                updatePageAccessDetails(
                    isPubliclyAccessible,
                    isCloningPermitted
                ),
            ];

            if (!isPubliclyAccessible) {
                updateActions.push(saveChangesToIndividualsWithAccess());
            }

            const saveResults = await Promise.all(updateActions);

            return saveResults.reduce((acc, result) => acc && result, true);
        },
        [saveChangesToIndividualsWithAccess, updatePageAccessDetails]
    );

    const refreshPageAccess = useCallback(async () => {
        await refreshPage();
        await refreshAccessRecipients();
    }, [refreshAccessRecipients, refreshPage]);

    return {
        accessRecipients: accessRecipients,
        publicPageLink: publicPageLink,
        clearErrors: clearErrors,
        errors: errors,
        isPubliclyAccessible: isPubliclyAccessible,
        isCloningPermitted: isCloningPermitted,
        loading: loading,
        saveChanges: saveChanges,
        stageNewRecipients: stageNewRecipients,
        removeRecipient: removeRecipient,
        refreshPageAccess,
        clearUnpersistedChanges,
    };
};
